import { AssetState, FilterData } from '@/api/generatedApi';
import { DATA_GRID_LAZY_PAGE_SIZE } from '@/components/DataGrid/definitions';
import { useDataGridFetcher } from '@/hooks/api/useDataGridFetcher';
import { useSearchFilters } from '@/hooks/useSearchFilters';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';

export const LIST_ASSETS_BY_STATE_QUERY_KEY = 'assets.listAssetsByState';

export const getAssetsByStateQueryKey = ({
  assetState,
  page,
  perPage,
  tenantId,
  folders,
  extensions,
}: {
  assetState?: AssetState;
  page?: string | number;
  perPage?: string | number;
  tenantId: string;
  folders?: string;
  extensions?: string;
}) => [
  LIST_ASSETS_BY_STATE_QUERY_KEY,
  {
    assetState,
    tenantId,
    page: +(page ?? 1),
    perPage: +(perPage ?? DATA_GRID_LAZY_PAGE_SIZE),
    folders,
    extensions,
  },
];

const queryFunction =
  ({
    assetState,
    page,
    perPage,
    tenantId,
    folders,
    extensions,
  }: {
    assetState: AssetState;
    page?: string | number;
    perPage?: string | number;
    tenantId: string;
    folders?: string;
    extensions?: string;
  }) =>
  () =>
    api.assets.listAssetsByState({
      tenantId,
      assetState,
      page: +(page ?? 1),
      perPage: +(perPage ?? DATA_GRID_LAZY_PAGE_SIZE),
      folders,
      extensions,
    });

export const useFetchAssetsByState = ({ assetState }: { assetState: AssetState }) => {
  const { getTenantId } = useTenants();
  const { selectedFilters } = useSearchFilters();

  const foldersParameter = selectedFilters.folders.length > 0 ? selectedFilters.folders.join(',') : undefined;
  const extensionsParameter = selectedFilters.extensions.length > 0 ? selectedFilters.extensions.join(',') : undefined;

  return useDataGridFetcher({
    queryKey: parameters =>
      getAssetsByStateQueryKey({
        tenantId: getTenantId(),
        assetState,
        folders: foldersParameter,
        extensions: extensionsParameter,
        ...parameters,
      }),
    queryFn: parameters =>
      queryFunction({
        tenantId: getTenantId(),
        assetState,
        folders: foldersParameter,
        extensions: extensionsParameter,
        ...parameters,
      }),
    transformResponse: data => ({
      matches: data.assets.map(asset => ({ asset, matchPercentage: undefined })),
      pageData: data.pageData,
      filterData: data.filterData as Partial<FilterData> | undefined,
    }),
  });
};
